import React, { useEffect, useState } from 'react';
import NavBar from '../../components/Navbar/NavBar';
import Footer from '../../components/Footer';
import Card from '../../components/Cards/Card';
import SideMenu from '../../components/SideMenu';
import useDocTitle from '../../utils/useDocTitle';
import api from '../../utils/api.js';
import { useLanguage } from '../../context/LanguageContext';

const Resources = () => {
  const { language } = useLanguage(); // Use language context
  useDocTitle(language === 'English' ? 'Resources' : 'Burimet');

  const [resources, setResources] = useState([]);
  const [visibleCount, setVisibleCount] = useState(3);
  const [mainPageData, setMainPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch main page data
        const mainPageResponse = await api.get('/resources_mainpage');
        const mainData = mainPageResponse.data.find(
          (data) => data.page_language[0].name === language
        );

        if (mainData) {
          setMainPageData({
            title: mainData.title.rendered || (language === 'English' ? 'Resources' : 'Burimet'),
            backgroundImage: mainData.main_image.guid || '/documents/resources/coverImage.JPG',
            backgroundImageAlt:
              mainData.main_image.post_excerpt ||
              (language === 'English' ? 'Cover image for Resources' : 'Imazhi i mbulesës për Burimet'),
          });
        }

        // Fetch and combine articles and studies
        const [articlesResponse, studiesResponse] = await Promise.all([
          api.get('/articles'),
          api.get('/studies'),
        ]);

        const filteredArticles = articlesResponse.data.filter(
          (article) => article.page_language[0].name === language
        );

        const filteredStudies = studiesResponse.data.filter(
          (study) => study.study_language[0].name === language
        );

        const combinedResources = [
          ...filteredArticles.map((article) => ({
            id: article.id,
            title: article.title.rendered,
            description: article.description || article.excerpt.rendered || '',
            imgSrc: article.main_image?.guid || '/path/to/default-image.jpg',
            imgAlt: article.main_image?.post_excerpt,
            linkPath: `/resources/${article.id}`,
            category: 'articles',
          })),
          ...filteredStudies.map((study) => ({
            id: study.id,
            title: study.title.rendered,
            description: study.description || '',
            imgSrc: study.main_image?.guid || '/path/to/default-image.jpg',
            imgAlt: study.main_image?.post_excerpt,
            downloadLink: study.document.guid,
            category: 'studies',
          })),
        ];

        setResources(combinedResources);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [language]);

  if (loading) {
    return <div>{language === 'English' ? 'Loading...' : 'Duke u ngarkuar...'}</div>;
  }

  const categorizedResources = resources.reduce((acc, resource) => {
    if (!acc[resource.category]) acc[resource.category] = [];
    acc[resource.category].push(resource);
    return acc;
  }, {});

  const menuItems = [
    { href: '#overview', label: language === 'English' ? 'Overview' : 'Përmbledhje' },
    ...Object.keys(categorizedResources).map((category) => ({
      href: `#${category}`,
      label:
        category === 'articles'
          ? language === 'English'
            ? 'Articles'
            : 'Artikuj'
          : language === 'English'
          ? 'Studies'
          : 'Studime',
    })),
  ];

  return (
    <>
      <NavBar />
      {mainPageData && (
        <div
          className="relative w-full h-96 bg-cover bg-center flex items-center justify-center"
          style={{
            backgroundImage: `url(${mainPageData.backgroundImage})`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            height: '550px',
          }}
          role="img"
          aria-label={mainPageData.backgroundImageAlt}
        >
          <div className="absolute inset-0 bg-black opacity-60"></div>
          <div className="relative z-10 text-center text-white px-4">
            <h1 className="text-5xl font-bold" tabIndex="0">
              {mainPageData.title}
            </h1>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row min-h-screen">
        <aside
          className="w-full md:w-1/4 bg-gray-100 px-4 md:px-8 py-8 md:py-12 sticky top-16 md:h-screen"
          aria-label={
            language === 'English'
              ? 'Side menu for resources'
              : 'Menuja anësore për burimet'
          }
        >
          <SideMenu
            backLink="/"
            backText={language === 'English' ? 'MAIN PAGE' : 'FAQJA KRYESORE'}
            menuItems={menuItems}
          />
        </aside>

        <div className="w-full md:w-3/4 py-8 px-4 md:px-6">
          <section id="overview" className="mb-12">
            <h2 className="text-3xl font-semibold mb-6" tabIndex="0">
              {language === 'English' ? 'Overview' : 'Përmbledhje'}
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              {language === 'English'
                ? 'Explore various categorized resources to improve accessibility and knowledge sharing.'
                : 'Eksploroni burime të ndryshme të kategorizuara për të përmirësuar aksesin dhe shkëmbimin e njohurive.'}
            </p>
          </section>

          {Object.keys(categorizedResources).map((category) => (
            <section key={category} id={category} className="mb-12">
              <h2 className="text-3xl font-semibold mb-6" tabIndex="0">
                {category === 'articles'
                  ? language === 'English'
                    ? 'Articles'
                    : 'Artikuj'
                  : language === 'English'
                  ? 'Studies'
                  : 'Studime'}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {categorizedResources[category].slice(0, visibleCount).map((resource) =>
                  category === 'studies' ? (
                    <div
                      key={resource.id}
                      className="card border-primary rounded-lg shadow-md"
                    >
                      <img
                        src={resource.imgSrc}
                        alt={resource.imgAlt}
                        className="card-image rounded-t-lg"
                      />
                      <div className="card-content p-4">
                        <h3 className="card-title text-lg font-semibold mb-2">
                          {resource.title}
                        </h3>
                        <p className="card-description text-sm text-gray-600">
                          {resource.description}
                        </p>
                        <button
                          onClick={() => {
                            const anchor = document.createElement('a');
                            anchor.href = resource.downloadLink;
                            anchor.download = '';
                            document.body.appendChild(anchor);
                            anchor.click();
                            document.body.removeChild(anchor);
                          }}
                          className="btn-custom px-6 py-2"
                          aria-label={
                            language === 'English'
                              ? `Download the study for ${resource.title}`
                              : `Shkarko studimin për ${resource.title}`
                          }
                        >
                          {language === 'English' ? 'Download' : 'Shkarko'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <Card
                      key={resource.id}
                      id={resource.id}
                      title={resource.title}
                      description={resource.description}
                      imgSrc={resource.imgSrc}
                      imgAlt={resource.imgAlt}
                      linkText={language === 'English' ? 'Read More' : 'Lexo më shumë'}
                      linkPath={resource.linkPath}
                    />
                  )
                )}
              </div>
              {visibleCount < categorizedResources[category].length && (
                <div className="flex justify-center mt-8">
                  <button
                    onClick={() => setVisibleCount(visibleCount + 3)}
                    className="btn-custom px-6 py-2"
                    aria-label={
                      language === 'English'
                        ? `View more articles in the ${category} category`
                        : `Shiko më shumë artikuj për kategorinë ${category}`
                    }
                  >
                    {language === 'English' ? 'View More' : 'Shiko më shumë'}
                  </button>
                </div>
              )}
            </section>
          ))}
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Resources;